import type { EmployeePhoto } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef, useId, useState } from 'react';
import { api } from '@/lib/api';
import { initials } from '@/lib/format';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const buttonStyles: Record<ButtonVariant, string> = {
  primary: 'bg-graphite text-white hover:bg-graphite-2 disabled:bg-steel',
  secondary: 'bg-surface text-ink border border-line-strong hover:bg-sunken',
  ghost: 'text-ink-soft hover:bg-sunken hover:text-ink',
  danger: 'bg-surface text-danger border border-danger/40 hover:bg-danger-soft',
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean; size?: 'sm' | 'md' }
>(function Button(
  { variant = 'primary', loading, size = 'md', className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-70',
        size === 'md' ? 'h-9 px-4 text-[14px]' : 'h-8 px-3 text-[13px]',
        buttonStyles[variant],
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby'?: string }) => ReactNode;
}) {
  const id = useId();
  const msgId = `${id}-msg`;
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children({
        id,
        'aria-invalid': Boolean(error),
        'aria-describedby': error || hint ? msgId : undefined,
      })}
      {(error || hint) && (
        <p id={msgId} className={clsx('mt-1 text-[12.5px]', error ? 'text-danger' : 'text-steel')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={clsx('input', className)} {...rest} />;
});

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={clsx('input pr-8', className)} {...rest}>
      {children}
    </select>
  );
}

/** Фото сотрудников одним запросом на всё приложение (кэш 5 минут). */
const photosQuery = {
  queryKey: ['org', 'photos'],
  queryFn: () => api<EmployeePhoto[]>('/org/photos'),
  staleTime: 5 * 60_000,
};

/**
 * Аватар: фото сотрудника, если оно загружено (по учётке или карточке), иначе инициалы
 * на приглушённом стальном фоне. Цвет — стабильный для человека.
 */
export function Avatar({
  name,
  size = 32,
  className,
  userId,
  employeeId,
}: {
  name: string;
  size?: number;
  className?: string;
  userId?: string | null;
  employeeId?: string | null;
}) {
  const photos = useQuery({ ...photosQuery, enabled: Boolean(userId || employeeId) });
  const photo = photos.data?.find(
    (p) => (userId && p.userId === userId) || (employeeId && p.employeeId === employeeId),
  );
  const [broken, setBroken] = useState<string | null>(null);
  if (photo && broken !== photo.fileId)
    return (
      <img
        src={`/api/files/${photo.fileId}`}
        alt=""
        aria-hidden
        loading="lazy"
        onError={() => setBroken(photo.fileId)}
        className={clsx('inline-block shrink-0 rounded-full object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  const hues = [205, 190, 220, 170, 235, 150];
  const hue = hues[[...name].reduce((s, c) => s + c.charCodeAt(0), 0) % hues.length];
  return (
    <span
      aria-hidden
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: `hsl(${hue} 22% 38%)`,
      }}
    >
      {initials(name)}
    </span>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
  children: ReactNode;
}) {
  const tones = {
    neutral: 'bg-sunken text-ink-soft',
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-signal-soft text-[#7a5b00]',
    danger: 'bg-danger-soft text-danger',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ label = 'Загрузка' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-10 text-steel justify-center" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Не удалось загрузить данные';
  return (
    <div
      role="alert"
      className="rounded-[var(--radius-control)] border border-danger/30 bg-danger-soft px-3 py-2 text-danger"
    >
      {message}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.015em]">{title}</h1>
        {description && <p className="mt-1 max-w-[70ch] text-steel">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Dl({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(120px,180px)_1fr] gap-x-6 gap-y-2.5">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-steel">{k}</dt>
          <dd className="min-w-0 break-words">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Переключатель вкладок в одну строку (Мои / Все, Открытые / Завершённые). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="inline-flex max-w-full overflow-x-auto rounded-md border border-line-strong bg-surface p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'h-8 shrink-0 rounded px-3 text-[13.5px] whitespace-nowrap',
            value === o.value ? 'bg-graphite text-white' : 'text-ink-soft hover:bg-sunken',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={clsx('input h-auto min-h-[84px] py-2 leading-snug', className)} {...rest} />;
}
